"use client";

// The Easter effect: a few chicks walk around and stop now and then to peck.
// A click makes a chick hop away in an arc off the screen. One
// requestAnimationFrame loop with `transform` only; the legs and the pecking
// head are CSS animations.

import { useEffect, useRef } from "react";
import styles from "./hogtid.module.css";

const ANTAL = 4;
const STORLEK = 40;
const TYNGD = 1500; // px / s², the hop's gravity

interface Kyckling {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** "ga" walking, "picka" pecking in place, "hopp" leaving, "borta" gone */
  lage: "ga" | "picka" | "hopp" | "borta";
  bytTill: number; // when the walk/peck state changes
  riktning: number; // walking heading, radians
}

/** Hop away from the nearest side, high and fast (called from the click). */
function hoppStart(x: number): Pick<Kyckling, "vx" | "vy" | "lage"> {
  const bort = x < window.innerWidth / 2 ? -1 : 1;
  return { vx: bort * (260 + Math.random() * 160), vy: -(620 + Math.random() * 200), lage: "hopp" };
}

export default function Kycklingar() {
  const elRef = useRef<(HTMLButtonElement | null)[]>([]);
  const kycklingar = useRef<Kyckling[]>([]);

  useEffect(() => {
    const b = () => window.innerWidth;
    const h = () => window.innerHeight;
    const nu0 = performance.now();
    kycklingar.current = Array.from({ length: ANTAL }, () => ({
      x: STORLEK + Math.random() * Math.max(1, b() - 3 * STORLEK),
      y: STORLEK + Math.random() * Math.max(1, h() - 3 * STORLEK),
      vx: 0,
      vy: 0,
      lage: "ga",
      bytTill: nu0 + 1000 + Math.random() * 3000,
      riktning: Math.random() < 0.5 ? 0 : Math.PI,
    }));
    let forra = nu0;
    let id = 0;
    const steg = (nu: number) => {
      // ~30 updates a second is smooth enough at walking pace and halves the work.
      if (nu - forra < 30) {
        id = requestAnimationFrame(steg);
        return;
      }
      const dt = Math.min(0.08, (nu - forra) / 1000);
      forra = nu;
      const bredd = b();
      const hojd = h();
      kycklingar.current.forEach((k, i) => {
        const el = elRef.current[i];
        if (!el || k.lage === "borta") return;
        if (k.lage === "hopp") {
          k.vy += TYNGD * dt;
          k.x += k.vx * dt;
          k.y += k.vy * dt;
          if (k.y > hojd + STORLEK || k.x < -STORLEK * 2 || k.x > bredd + STORLEK) {
            k.lage = "borta";
            el.style.display = "none";
            return;
          }
        } else {
          if (nu > k.bytTill) {
            // Alternate: walk a while, peck a while.
            k.lage = k.lage === "ga" ? "picka" : "ga";
            k.bytTill = nu + (k.lage === "picka" ? 900 + Math.random() * 1600 : 1500 + Math.random() * 3500);
            if (k.lage === "ga" && Math.random() < 0.4) k.riktning += Math.PI + (Math.random() - 0.5);
          }
          if (k.lage === "ga") {
            k.riktning += (Math.random() - 0.5) * 1.2 * dt;
            // Turn back from the edges.
            if (k.x < STORLEK && Math.cos(k.riktning) < 0) k.riktning = Math.PI - k.riktning;
            if (k.x > bredd - 2 * STORLEK && Math.cos(k.riktning) > 0) k.riktning = Math.PI - k.riktning;
            if (k.y < STORLEK && Math.sin(k.riktning) < 0) k.riktning = -k.riktning;
            if (k.y > hojd - 2 * STORLEK && Math.sin(k.riktning) > 0) k.riktning = -k.riktning;
            k.x += Math.cos(k.riktning) * 34 * dt;
            k.y += Math.sin(k.riktning) * 34 * dt;
          }
          el.dataset.lage = k.lage;
        }
        // Faces the way it walks (the drawing faces right).
        const vand = (k.lage === "hopp" ? k.vx : Math.cos(k.riktning)) < 0 ? -1 : 1;
        el.style.transform = `translate(${k.x}px, ${k.y}px) scaleX(${vand})`;
      });
      id = requestAnimationFrame(steg);
    };
    id = requestAnimationFrame(steg);
    return () => cancelAnimationFrame(id);
  }, []);

  const skramma = (i: number) => {
    const k = kycklingar.current[i];
    const el = elRef.current[i];
    if (!k || !el || k.lage === "hopp" || k.lage === "borta") return;
    Object.assign(k, hoppStart(k.x));
    el.dataset.lage = "hopp";
  };

  return (
    <>
      {Array.from({ length: ANTAL }, (_, i) => (
        <button
          key={i}
          ref={(el) => {
            elRef.current[i] = el;
          }}
          type="button"
          tabIndex={-1}
          className={`${styles.spindel} ${styles.kyckling}`}
          title="Skrämma kycklingen så att den skuttar iväg"
          onClick={(e) => {
            e.stopPropagation();
            skramma(i);
          }}
        >
          <svg viewBox="0 0 40 40" width={STORLEK} height={STORLEK}>
            <g className={styles.ben} stroke="var(--color-hogtid-pumpa)" strokeWidth="2" strokeLinecap="round" fill="none">
              <path className={styles.benA} d="M15 32 L13 38 M13 38 L10 38" />
              <path className={styles.benB} d="M21 32 L23 38 M23 38 L20 38" />
            </g>
            <ellipse cx="18" cy="24" rx="12" ry="10" fill="var(--color-hogtid-fjader-gul)" />
            <path d="M10 22 C13 19 19 20 20 25 C16 28 11 27 10 22 Z" fill="var(--color-hogtid-saffran)" />
            <g className={styles.huvud}>
              <circle cx="27" cy="14" r="7.5" fill="var(--color-hogtid-fjader-gul)" />
              <circle cx="29.5" cy="12.5" r="1.3" fill="var(--color-hogtid-choklad)" />
              <path d="M34 13.5 L39.5 15.5 L34 17.5 Z" fill="var(--color-hogtid-pumpa)" />
              <path d="M25 6.8 C25.5 4.6 27.5 4.2 28 6.4" fill="none" stroke="var(--color-hogtid-saffran)" strokeWidth="1.4" strokeLinecap="round" />
            </g>
          </svg>
        </button>
      ))}
    </>
  );
}
